import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  SpaceBetween,
  Container,
  Header,
  Table,
  Box,
  Button,
  Input,
  Select,
  DatePicker,
  FormField,
  Icon,
  Grid,
} from "@amzn/awsui-components-react";
import { useGetTemplateQuery } from "../templates-table/endpoints";
import {
  useGetScanResultQuery,
  useGetScanjobQuery,
} from "pages/draft-overview/endpoints";
import { CreateTaskGroupModal } from "../create-taskgroup-modal/CreateTaskgroupModal";
import { CreateTaskGroupFormFields } from "models/schemas";
import CreateTaskModal from "../create-task-modal/CreateTaskModal";
import {
  TaskSuggestionsProps,
  TaskSuggestionGroup,
  TaskSuggestion,
} from "./definitions";
import { useGaiaObjectUiContent } from "features/ui-content";
import { createTaskTableColumns } from "./column-definitions";
import {
  createTaskGroupFromData,
  parseTemplateData,
  extractScanTaskData,
  hasScanTaskData,
  sortTaskGroupsByOrder,
  sortTasksByOrder,
  getNextTaskGroupOrder,
  getNextTaskOrder,
} from "./utils";
import {
  formatDateValue,
  toUnixEpochTime,
  normalizeDateFormat,
} from "utils/date-format";
import styles from "./style.module.scss";

const TaskSuggestions: React.FC<TaskSuggestionsProps> = ({
  shortId,
  templateId,
  teamName,
  onTaskDataChange,
  category,
}) => {
  const [localData, setLocalData] = useState<TaskSuggestionGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const isInitialLoadRef = useRef(true);

  const taskGroups = localData;

  const setTaskGroups = useCallback(
    (newTaskGroups: TaskSuggestionGroup[]) => {
      setLocalData(newTaskGroups);
      if (onTaskDataChange && !isInitialLoadRef.current) {
        const sortedTaskGroups = sortTaskGroupsByOrder(newTaskGroups).map(
          (tg) => ({
            ...tg,
            tasks: sortTasksByOrder(tg.tasks),
          }),
        );
        onTaskDataChange(sortedTaskGroups);
        setHasFormChanges(true);
      }
    },
    [onTaskDataChange],
  );

  const [expandedTaskGroups, setExpandedTaskGroups] = useState<Set<string>>(
    new Set(),
  );
  const [editingTaskGroup, setEditingTaskGroup] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  const [selectedTasks, setSelectedTasks] = useState<{
    [taskGroupId: string]: Set<string>;
  }>({});
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [currentTaskGroupId, setCurrentTaskGroupId] = useState<string>("");
  const [showCreateTaskGroupModal, setShowCreateTaskGroupModal] =
    useState(false);
  const [operationInProgress, setOperationInProgress] = useState<{
    [taskGroupId: string]: boolean;
  }>({});
  const uiContent: any = useGaiaObjectUiContent(category);

  const { data: scanJobData } = useGetScanjobQuery(
    { shortId: shortId!, trackOpen: false },
    { skip: !shortId },
  );

  const { data: scanResultData, isLoading: scanResultLoading } =
    useGetScanResultQuery({ shortId: shortId! }, { skip: !shortId });

  const actualTemplateId = !templateId
    ? ""
    : (templateId.split("#").pop() || "")
        .replace(/\//g, "_")
        .replace(/ /g, "_");

  const { data: templateData, isLoading: templateLoading } =
    useGetTemplateQuery(
      {
        templateName: actualTemplateId || "",
        teamName: teamName || "",
      },
      {
        skip: !actualTemplateId || !teamName,
      },
    );

  const handleTaskCreation = useCallback(
    (taskData: any) => {
      const defaultOwner = scanJobData?.scanJob?.createdBy || "";
      const currentTaskGroup = taskGroups.find(
        (tg) => tg.id === currentTaskGroupId,
      );
      const nextOrder = currentTaskGroup
        ? getNextTaskOrder(currentTaskGroup.tasks)
        : 0;

      const newTask: TaskSuggestion = {
        id: `task-${Date.now()}`,
        name: taskData.taskName || "New Task",
        description: taskData.description || "",
        type: taskData.inquiryType || "",
        status: taskData.status || "Not started",
        dueDate: taskData.dueDateString || "",
        owners: taskData.owners || defaultOwner,
        actualStart: taskData.actualStartDateString || "",
        completionDate: taskData.actualCompletionDateString || "",
        plannedStart: taskData.plannedStartDateString || "",
        complexity: taskData.complexity || "",
        priority: taskData.priority || "",
        milestone: Boolean(taskData.milestone),
        order: nextOrder,
      };

      const updatedTaskGroups = taskGroups.map((tg) =>
        tg.id === currentTaskGroupId
          ? { ...tg, tasks: [...tg.tasks, newTask] }
          : tg,
      );

      setTaskGroups(updatedTaskGroups);

      setShowCreateTaskModal(false);
      setCurrentTaskGroupId("");
    },
    [taskGroups, currentTaskGroupId, setTaskGroups, scanJobData],
  );

  const buildTemplateGroups = useCallback((): TaskSuggestionGroup[] => {
    if (!templateData || !actualTemplateId) return [];
    const defaultOwner = scanJobData?.scanJob?.createdBy || "Unassigned";
    const apiResponse = parseTemplateData(templateData);
    if (apiResponse.length === 0) return [];
    return apiResponse.map((templateTaskGroup: any, index: number) =>
      createTaskGroupFromData(
        templateTaskGroup,
        index,
        defaultOwner,
        "template",
      ),
    );
  }, [templateData, actualTemplateId, scanJobData]);

  const buildScanGroup = useCallback(
    (orderOffset: number): TaskSuggestionGroup | null => {
      if (!scanResultData?.scanTaskResult) return null;
      if (!hasScanTaskData(scanResultData.scanTaskResult)) return null;
      const defaultOwner = scanJobData?.scanJob?.createdBy || "";
      return extractScanTaskData(
        scanResultData.scanTaskResult,
        defaultOwner,
        orderOffset,
      );
    },
    [scanResultData, scanJobData],
  );

  useEffect(() => {
    if (hasFormChanges) {
      setLoading(false);
      return;
    }
    // Wait for both queries to settle before merging — first write flips hasFormChanges and locks out the other.
    const templateReady = !actualTemplateId || !teamName || !templateLoading;
    const scanReady = !shortId || !scanResultLoading;
    if (!templateReady || !scanReady) {
      return;
    }
    try {
      setLoading(true);
      const templateGroups = buildTemplateGroups();
      const scanGroup = buildScanGroup(templateGroups.length);
      const combined: TaskSuggestionGroup[] = [
        ...templateGroups,
        ...(scanGroup ? [scanGroup] : []),
      ];
      isInitialLoadRef.current = false;
      setTaskGroups(combined);
    } catch (error) {
      setLocalData([]);
      console.error("Error processing task data:", error);
    } finally {
      setLoading(false);
    }
  }, [
    actualTemplateId,
    teamName,
    shortId,
    templateLoading,
    scanResultLoading,
    hasFormChanges,
    buildTemplateGroups,
    buildScanGroup,
  ]);

  useEffect(() => {
    if (taskGroups.length > 0) {
      const taskGroupIds = taskGroups.map((tg) => tg.id);
      setExpandedTaskGroups(new Set(taskGroupIds));
    }
  }, [taskGroups]);

  const handleRemoveAll = useCallback(() => {
    const updatedTaskGroups: TaskSuggestionGroup[] = [];
    setTaskGroups(updatedTaskGroups);
    setHasFormChanges(true);

    if (onTaskDataChange) {
      onTaskDataChange(updatedTaskGroups);
    }
  }, [setTaskGroups, onTaskDataChange]);

  const hasTaskGroups = taskGroups.length > 0;
  const hasAnyTasks = taskGroups.some((tg) => tg.tasks.length > 0);
  const hasTaskGroupsOrTasks = hasTaskGroups || hasAnyTasks;

  const handleAddTaskGroup = useCallback(() => {
    setShowCreateTaskGroupModal(true);
  }, []);

  const handleTaskGroupCreation = useCallback(
    (values: CreateTaskGroupFormFields) => {
      const nextOrder = getNextTaskGroupOrder(taskGroups);

      const newTaskGroup: TaskSuggestionGroup = {
        id: `tg-${Date.now()}`,
        name: values.taskName || "New Task Group",
        description: values.taskName || "New Task Group",
        status: "pending",
        type: values.taskGroupType || "",
        dueDate: values.dueDateString || "",
        order: nextOrder,
        tasks: [],
      };

      const updatedTaskGroups = [...taskGroups, newTaskGroup];
      setTaskGroups(updatedTaskGroups);
      setExpandedTaskGroups(
        (prev) => new Set([...Array.from(prev), newTaskGroup.id]),
      );

      setShowCreateTaskGroupModal(false);
    },
    [taskGroups, setTaskGroups],
  );

  const handleRemoveTaskGroup = useCallback(
    (taskGroupId: string) => {
      const updatedTaskGroups = taskGroups.filter(
        (tg) => tg.id !== taskGroupId,
      );
      setTaskGroups(updatedTaskGroups);
      setExpandedTaskGroups((prev) => {
        const newSet = new Set(prev);
        newSet.delete(taskGroupId);
        return newSet;
      });

      if (onTaskDataChange) {
        onTaskDataChange(updatedTaskGroups);
      }
    },
    [taskGroups, setTaskGroups, onTaskDataChange],
  );

  const handleUpdateTask = useCallback(
    (taskGroupId: string, taskId: string, field: string, value: string) => {
      const updatedTaskGroups = taskGroups.map((tg) =>
        tg.id === taskGroupId
          ? {
              ...tg,
              tasks: tg.tasks.map((t) =>
                t.id === taskId ? { ...t, [field]: value } : t,
              ),
            }
          : tg,
      );

      setTaskGroups(updatedTaskGroups);

      if (onTaskDataChange) {
        onTaskDataChange(updatedTaskGroups);
      }
    },
    [taskGroups, setTaskGroups, onTaskDataChange],
  );

  const handleUpdateTaskGroup = useCallback(
    (taskGroupId: string, field: string, value: string) => {
      const updatedTaskGroups = taskGroups.map((tg) => {
        if (tg.id === taskGroupId) {
          const updatedTaskGroup = { ...tg, [field]: value };
          if (field === "name") {
            updatedTaskGroup.description = value;
          }
          return updatedTaskGroup;
        }
        return tg;
      });

      setTaskGroups(updatedTaskGroups);

      if (onTaskDataChange) {
        onTaskDataChange(updatedTaskGroups);
      }
    },
    [taskGroups, setTaskGroups, onTaskDataChange],
  );

  const startEditing = useCallback(
    (taskGroupId: string, field: string, currentValue: string) => {
      setEditingTaskGroup(taskGroupId);
      setEditingField(field);
      setEditingValue(currentValue);
    },
    [],
  );

  const saveEdit = useCallback(() => {
    if (editingTaskGroup && editingField) {
      handleUpdateTaskGroup(editingTaskGroup, editingField, editingValue);
    }
    setEditingTaskGroup(null);
    setEditingField(null);
    setEditingValue("");
  }, [editingTaskGroup, editingField, editingValue, handleUpdateTaskGroup]);

  const cancelEdit = useCallback(() => {
    setEditingTaskGroup(null);
    setEditingField(null);
    setEditingValue("");
  }, []);

  const isEditing = useCallback(
    (taskGroupId: string, field: string) => {
      return editingTaskGroup === taskGroupId && editingField === field;
    },
    [editingTaskGroup, editingField],
  );

  const toggleTaskGroup = useCallback((taskGroupId: string) => {
    setExpandedTaskGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskGroupId)) {
        newSet.delete(taskGroupId);
      } else {
        newSet.add(taskGroupId);
      }
      return newSet;
    });
  }, []);

  const hasSelectedTasks = useCallback(
    (taskGroupId: string): boolean => {
      return (selectedTasks[taskGroupId]?.size || 0) > 0;
    },
    [selectedTasks],
  );

  const clearSelectionForTaskGroup = useCallback((taskGroupId: string) => {
    setSelectedTasks((prev) => ({
      ...prev,
      [taskGroupId]: new Set(),
    }));
  }, []);

  const validateSelectedTasks = useCallback(
    (taskGroupId: string): string[] => {
      const taskGroup = taskGroups.find((tg) => tg.id === taskGroupId);
      if (!taskGroup) return [];

      const existingTaskIds = new Set(taskGroup.tasks.map((t) => t.id));
      const selectedTaskIds = Array.from(selectedTasks[taskGroupId] || []);

      const validSelectedIds = selectedTaskIds.filter((id) =>
        existingTaskIds.has(id),
      );

      if (validSelectedIds.length !== selectedTaskIds.length) {
        setSelectedTasks((prev) => ({
          ...prev,
          [taskGroupId]: new Set(validSelectedIds),
        }));
      }

      return validSelectedIds;
    },
    [taskGroups, selectedTasks],
  );

  const handleBulkMarkAsMilestone = useCallback(
    async (taskGroupId: string) => {
      if (operationInProgress[taskGroupId]) return;

      try {
        setOperationInProgress((prev) => ({ ...prev, [taskGroupId]: true }));

        const validSelectedIds = validateSelectedTasks(taskGroupId);
        if (validSelectedIds.length === 0) {
          setOperationInProgress((prev) => ({ ...prev, [taskGroupId]: false }));
          return;
        }

        const updatedTaskGroups = taskGroups.map((tg) =>
          tg.id === taskGroupId
            ? {
                ...tg,
                tasks: tg.tasks.map((t) =>
                  validSelectedIds.includes(t.id)
                    ? { ...t, milestone: !t.milestone }
                    : t,
                ),
              }
            : tg,
        );

        setTaskGroups(updatedTaskGroups);
        clearSelectionForTaskGroup(taskGroupId);
      } catch (error) {
        console.error("Error in bulk mark as milestone:", error);
      } finally {
        setOperationInProgress((prev) => ({ ...prev, [taskGroupId]: false }));
      }
    },
    [
      operationInProgress,
      validateSelectedTasks,
      taskGroups,
      setTaskGroups,
      clearSelectionForTaskGroup,
    ],
  );

  const handleBulkRemoveTask = useCallback(
    async (taskGroupId: string) => {
      if (operationInProgress[taskGroupId]) return;

      try {
        setOperationInProgress((prev) => ({ ...prev, [taskGroupId]: true }));

        const validSelectedIds = validateSelectedTasks(taskGroupId);
        if (validSelectedIds.length === 0) {
          setOperationInProgress((prev) => ({ ...prev, [taskGroupId]: false }));
          return;
        }

        const updatedTaskGroups = taskGroups.map((tg) =>
          tg.id === taskGroupId
            ? {
                ...tg,
                tasks: tg.tasks.filter((t) => !validSelectedIds.includes(t.id)),
              }
            : tg,
        );

        setTaskGroups(updatedTaskGroups);
        clearSelectionForTaskGroup(taskGroupId);
      } catch (error) {
        console.error("Error in bulk remove task:", error);
      } finally {
        setOperationInProgress((prev) => ({ ...prev, [taskGroupId]: false }));
      }
    },
    [
      operationInProgress,
      validateSelectedTasks,
      taskGroups,
      setTaskGroups,
      clearSelectionForTaskGroup,
    ],
  );

  return (
    <SpaceBetween direction="vertical" size="m">
      <Header
        variant="h2"
        actions={
          <SpaceBetween direction="horizontal" size="s">
            <Button onClick={handleRemoveAll} disabled={!hasTaskGroupsOrTasks}>
              Remove all
            </Button>
            <Button variant="normal" onClick={handleAddTaskGroup}>
              Add task group
            </Button>
          </SpaceBetween>
        }
      >
        Tasks
      </Header>
      {loading ? (
        <Box textAlign="center">Loading task suggestions...</Box>
      ) : !hasTaskGroupsOrTasks ? (
        <Box textAlign="left" color="inherit">
          <SpaceBetween direction="vertical" size="m">
            <Box variant="p">
              Create the {category} now and add task groups and tasks later.
            </Box>
          </SpaceBetween>
        </Box>
      ) : (
        <SpaceBetween direction="vertical" size="m">
          {sortTaskGroupsByOrder(taskGroups).map((taskGroup) => (
            <Container key={taskGroup.id} variant="stacked">
              <SpaceBetween direction="vertical" size="s">
                <Header
                  variant="h3"
                  actions={
                    <Button
                      onClick={() => handleRemoveTaskGroup(taskGroup.id)}
                      variant="normal"
                    >
                      Remove task group
                    </Button>
                  }
                >
                  <Box variant="h2">{taskGroup.name}</Box>
                </Header>
                <Grid
                  gridDefinition={[
                    { colspan: 4 },
                    { colspan: 4 },
                    { colspan: 4 },
                  ]}
                >
                  <FormField label="Name">
                    {isEditing(taskGroup.id, "name") ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Input
                          value={editingValue}
                          onChange={({ detail }) =>
                            setEditingValue(detail.value)
                          }
                          onKeyDown={(event) => {
                            if (event.detail.key === "Enter") {
                              saveEdit();
                            } else if (event.detail.key === "Escape") {
                              cancelEdit();
                            }
                          }}
                          autoFocus
                        />
                        <Button
                          variant="icon"
                          iconName="check"
                          onClick={saveEdit}
                        />
                        <Button
                          variant="icon"
                          iconName="close"
                          onClick={cancelEdit}
                        />
                      </div>
                    ) : (
                      <SpaceBetween
                        direction="horizontal"
                        size="xs"
                        alignItems="center"
                      >
                        <Box variant="p">{taskGroup.name}</Box>
                        <Button
                          variant="icon"
                          iconName="edit"
                          ariaLabel="Edit task group name"
                          onClick={() =>
                            startEditing(taskGroup.id, "name", taskGroup.name)
                          }
                        />
                      </SpaceBetween>
                    )}
                  </FormField>

                  <FormField label="Due date">
                    {isEditing(taskGroup.id, "dueDate") ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <DatePicker
                          value={editingValue}
                          onChange={({ detail }) =>
                            setEditingValue(detail.value)
                          }
                          placeholder="YYYY/MM/DD"
                          expandToViewport
                        />
                        <Button
                          variant="icon"
                          iconName="check"
                          onClick={saveEdit}
                        />
                        <Button
                          variant="icon"
                          iconName="close"
                          onClick={cancelEdit}
                        />
                      </div>
                    ) : (
                      <SpaceBetween
                        direction="horizontal"
                        size="xs"
                        alignItems="center"
                      >
                        <Box variant="p">
                          {taskGroup.dueDate
                            ? formatDateValue(
                                toUnixEpochTime(
                                  normalizeDateFormat(taskGroup.dueDate),
                                ),
                              )
                            : ""}
                        </Box>
                        <Button
                          variant="icon"
                          iconName="edit"
                          ariaLabel="Edit due date"
                          onClick={() =>
                            startEditing(
                              taskGroup.id,
                              "dueDate",
                              taskGroup.dueDate || "",
                            )
                          }
                        />
                      </SpaceBetween>
                    )}
                  </FormField>

                  <FormField label="Type">
                    {isEditing(taskGroup.id, "type") ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Select
                          selectedOption={
                            uiContent.taskGroupTypeOptions?.find(
                              (opt: any) => opt.value === editingValue,
                            ) || null
                          }
                          onChange={({ detail }) =>
                            setEditingValue(
                              detail.selectedOption.value || editingValue,
                            )
                          }
                          options={uiContent.taskGroupTypeOptions || []}
                          expandToViewport
                          filteringType="auto"
                        />
                        <Button
                          variant="icon"
                          iconName="check"
                          onClick={saveEdit}
                        />
                        <Button
                          variant="icon"
                          iconName="close"
                          onClick={cancelEdit}
                        />
                      </div>
                    ) : (
                      <SpaceBetween
                        direction="horizontal"
                        size="xs"
                        alignItems="center"
                      >
                        <Box variant="p">
                          {uiContent.taskGroupTypeOptions?.find(
                            (opt: any) => opt.value === taskGroup.type,
                          )?.label ||
                            taskGroup.type ||
                            ""}
                        </Box>
                        <Button
                          variant="icon"
                          iconName="edit"
                          ariaLabel="Edit type"
                          onClick={() =>
                            startEditing(
                              taskGroup.id,
                              "type",
                              taskGroup.type || "",
                            )
                          }
                        />
                      </SpaceBetween>
                    )}
                  </FormField>
                </Grid>
                <Header
                  actions={
                    <SpaceBetween direction="horizontal" size="s">
                      <Button
                        disabled={
                          !hasSelectedTasks(taskGroup.id) ||
                          operationInProgress[taskGroup.id]
                        }
                        loading={operationInProgress[taskGroup.id]}
                        onClick={() => handleBulkMarkAsMilestone(taskGroup.id)}
                      >
                        Mark as milestone
                      </Button>
                      <Button
                        disabled={
                          !hasSelectedTasks(taskGroup.id) ||
                          operationInProgress[taskGroup.id]
                        }
                        loading={operationInProgress[taskGroup.id]}
                        onClick={() => handleBulkRemoveTask(taskGroup.id)}
                      >
                        Remove task
                      </Button>
                      <Button
                        variant="normal"
                        onClick={() => {
                          setCurrentTaskGroupId(taskGroup.id);
                          setShowCreateTaskModal(true);
                        }}
                      >
                        Add task
                      </Button>
                    </SpaceBetween>
                  }
                >
                  <Button
                    variant="inline-link"
                    onClick={() => toggleTaskGroup(taskGroup.id)}
                    ariaLabel={`${
                      expandedTaskGroups.has(taskGroup.id)
                        ? "Collapse"
                        : "Expand"
                    } task group with ${taskGroup.tasks.length} tasks`}
                  >
                    <SpaceBetween
                      direction="horizontal"
                      size="xs"
                      alignItems="center"
                    >
                      <Icon
                        name={
                          expandedTaskGroups.has(taskGroup.id)
                            ? "caret-down-filled"
                            : "caret-right-filled"
                        }
                      />
                      <Box variant="span">({taskGroup.tasks.length})</Box>
                    </SpaceBetween>
                  </Button>
                </Header>

                {expandedTaskGroups.has(taskGroup.id) && (
                  <div className={styles.taskSuggestion}>
                    <Table
                      columnDefinitions={createTaskTableColumns(
                        uiContent,
                        teamName,
                      )}
                      items={sortTasksByOrder(taskGroup.tasks)}
                      loadingText="Loading tasks"
                      resizableColumns
                      variant="full-page"
                      submitEdit={(
                        item: TaskSuggestion,
                        column: any,
                        newValue: any,
                      ) => {
                        handleUpdateTask(
                          taskGroup.id,
                          item.id,
                          column.id,
                          newValue,
                        );
                      }}
                      selectionType="multi"
                      selectedItems={taskGroup.tasks.filter((task) =>
                        selectedTasks[taskGroup.id]?.has(task.id),
                      )}
                      onSelectionChange={({ detail }) => {
                        const newSelection = new Set<string>();
                        detail.selectedItems.forEach((item) => {
                          newSelection.add(item.id);
                        });
                        setSelectedTasks((prev) => ({
                          ...prev,
                          [taskGroup.id]: newSelection,
                        }));
                      }}
                      empty={
                        <Box textAlign="center" color="inherit">
                          <SpaceBetween direction="vertical" size="m">
                            <Box variant="p">
                              <b>No tasks</b>
                            </Box>
                            <Box variant="p" color="inherit">
                              No tasks found for this task group.
                            </Box>
                            <Button
                              onClick={() => {
                                setCurrentTaskGroupId(taskGroup.id);
                                setShowCreateTaskModal(true);
                              }}
                            >
                              Add task
                            </Button>
                          </SpaceBetween>
                        </Box>
                      }
                    />
                  </div>
                )}
              </SpaceBetween>
            </Container>
          ))}
        </SpaceBetween>
      )}

      <CreateTaskModal
        visible={showCreateTaskModal}
        setVisible={setShowCreateTaskModal}
        parentTaskId={currentTaskGroupId}
        parentCategory={teamName || ""}
        onCreate={handleTaskCreation}
      />

      <CreateTaskGroupModal
        visible={showCreateTaskGroupModal}
        setVisible={setShowCreateTaskGroupModal}
        onCreate={handleTaskGroupCreation}
        parentCategory={teamName || ""}
      />
    </SpaceBetween>
  );
};

export default TaskSuggestions;
